<script setup lang="ts">
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanel from "primevue/tabpanel";
import SignIn from "./SignIn.vue";
import SignUp from "./SignUp.vue";
import { ref, onMounted, onUnmounted } from "vue";
import Divider from "primevue/divider";
import { useAuthStore, useTokenStore, useAuthModal } from "@/features/auth/store";
import { useToast } from "primevue/usetoast";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const isClient = typeof window !== "undefined";

const value = ref("0");
const toast = useToast();

type ThirdPartyProvider = "google" | "vkontakte" | "yandex" | string;

type AuthResult =
  | { type: "THIRD_PARTY_AUTH_SUCCESS"; [key: string]: unknown }
  | { type: "THIRD_PARTY_AUTH_ERROR"; error?: string }
  | { type: "ACCOUNT_MERGED"; message?: string }
  | { type: string; [key: string]: unknown };

type ThirdPartySuccessResult = Extract<AuthResult, { type: "THIRD_PARTY_AUTH_SUCCESS" }>;

const isAuthResult = (payload: unknown): payload is AuthResult => {
  return typeof payload === "object" && payload !== null && "type" in payload;
};

const tabTheme = ref({
  tablist: {
    background: "transparent",
    borderColor: "rgba(215, 217, 219, 1)",
  },
  tab: {
    background: "transparent",
    activeBackground: "transparent",
    hoverBackground: "transparent",
    activeColor: "rgba(48, 48, 48, 1)",
    color: "rgba(145, 145, 145, 1)",
    borderColor: "rgba(215, 217, 219, 1)",
    padding: "12px 16px",
    activeBorderColor: "rgba(48, 48, 48, 1)",
  },
  tabpanel: {
    background: "transparent",
    padding: "0",
  },
  activeBar: {
    background: "rgba(48, 48, 48, 1)",
  },
});

const tabs = ref([
  {
    title: t("auth_wrapper.sign_in_tab"),
    value: "0",
  },
  {
    title: t("auth_wrapper.create_account_tab"),
    value: "1",
  },
]);

const handleThirdPartyLogin = async (provider: ThirdPartyProvider) => {
  try {
    await useAuthStore().thirdPartyAuth(provider);
    // Promise resolved = popup auth succeeded, handle on the main window side
    await handleThirdPartyAuthSuccess({ type: "THIRD_PARTY_AUTH_SUCCESS" });
  } catch (error) {
    const silentMessages = ['Popup closed by user', 'Third-party auth cancelled'];
    if (error instanceof Error && !silentMessages.includes(error.message)) {
      toast.add({
        severity: "error",
        summary: t("auth_wrapper.auth_error"),
        detail: error instanceof Error ? error.message : t("auth_wrapper.auth_error"),
        life: 5000,
      });
    }
  }
};

const handleThirdPartyAuthSuccess = async (data: ThirdPartySuccessResult) => {
  try {
    // Load tokens from localStorage into the main window's store
    const tokenStore = useTokenStore();
    const userTokens = isClient ? window.localStorage.getItem("userTokens") : null;
    if (userTokens) {
      const parsedTokens = JSON.parse(userTokens);
      tokenStore.accessToken = parsedTokens?.accessToken;
      tokenStore.refreshToken = parsedTokens?.refreshToken;
      tokenStore.expiresIn = parsedTokens?.expiresIn;
      tokenStore.refreshExpiresIn = parsedTokens?.refreshExpiresIn;

      // Schedule token refresh
      tokenStore.scheduleTokenRefresh();
    }

    // Get user info
    await useAuthStore().getUserInfo();

    // Show success toast
    toast.add({
      severity: "success",
      summary: t("auth_wrapper.login_success"),
      detail: t("auth_wrapper.login_success_detail"),
      life: 3000,
    });

    // Close modal
    useAuthModal().hideAuthModal();
  } catch (error) {
    console.error("Failed to complete third-party authentication:", error);
    toast.add({
      severity: "error",
      summary: t("auth_wrapper.error"),
      detail: t("auth_wrapper.auth_complete_error"),
      life: 5000,
    });
  }
};

const handleStorage = (event: StorageEvent) => {
  if (event.key !== "auth_result") {
    return;
  }

  try {
    const parsed = event.newValue ? JSON.parse(event.newValue) : null;

    if (!isAuthResult(parsed)) {
      return;
    }

    if (parsed.type === "THIRD_PARTY_AUTH_SUCCESS") {
      handleThirdPartyAuthSuccess(parsed);
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    } else if (parsed.type === "THIRD_PARTY_AUTH_ERROR") {
      toast.add({
        severity: "error",
        summary: t("auth_wrapper.auth_error"),
        detail: parsed.error || t("auth_wrapper.auth_error"),
        life: 5000,
      });
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    } else if (parsed.type === "ACCOUNT_MERGED") {
      toast.add({
        severity: "info",
        summary: t("auth_wrapper.account_merged"),
        detail: parsed.message || t("auth_wrapper.account_merged_detail"),
        life: 8000,
      });
      if (isClient) {
        window.localStorage.removeItem("auth_result");
      }
    }
  } catch (error) {
    console.error("Failed to parse auth result from localStorage:", error);
  }
};

onMounted(() => {
  window.addEventListener("storage", handleStorage);
});

onUnmounted(() => {
  window.removeEventListener("storage", handleStorage);
  // Abort an in-flight popup flow so its 1s poll interval dies with the modal.
  useAuthStore().cancelThirdPartyPopup();
});
</script>

<template>
  <div class="sign-in-sign-up-wrapper">
    <Tabs
      v-model:value="value"
      :pt="{
        panelContainer: {
          style: {
            padding: '0',
          },
        },
      }"
      :dt="tabTheme"
    >
      <TabList
        :pt="{
          root: {
            style: {
              background: 'transparent',
            },
          },
          tabList: {
            style: {
              width: '100%',
              background: 'transparent',
            },
          },
        }"
      >
        <Tab
          v-for="tab in tabs"
          :key="tab.title"
          :value="tab.value"
          :pt="{
            root: {
              style: {
                width: '50%',
              },
            },
          }"
        >
          {{ tab.title }}
        </Tab>
      </TabList>
      <TabPanel value="0">
        <sign-in />
      </TabPanel>
      <TabPanel value="1">
        <sign-up />
      </TabPanel>
    </Tabs>
    <div class="sign-in-bottom-content">
      <div class="sign-in__divider">
        <Divider
          type="solid"
          :pt="{
            content: {
              style: {
                background: 'rgba(245, 245, 247, 1)',
              },
            },
          }"
        >
          <span class="sign-in__divider-text title">{{
            t("auth_wrapper.or_sign_in_with")
          }}</span>
        </Divider>
      </div>
      <div class="sign-in_third_party">
        <div
          class="third-party-auth_item google"
          @click="handleThirdPartyLogin('google')"
        >
          <img src="/src/assets/img/Google.svg" alt="google auth" />
        </div>
        <div class="third-party-auth_item vk" @click="handleThirdPartyLogin('vkontakte')">
          <img src="/src/assets/img/VK.svg" alt="vk auth" />
        </div>
        <div
          class="third-party-auth_item yandex"
          @click="handleThirdPartyLogin('yandex')"
        >
          <img src="/src/assets/img/Yandex.svg" alt="yandex auth" />
        </div>
      </div>
    </div>
    <div class="have-dont-have-account">
      <p v-if="value === '0'">
        {{ t("auth_wrapper.no_account") }}
        <span
          :style="{
            fontWeight: '600',
            cursor: 'pointer',
          }"
          @click="value = '1'"
          >{{ t("auth_wrapper.create_account") }}
        </span>
      </p>

      <p v-if="value === '1'">
        {{ t("auth_wrapper.have_account") }}
        <span
          :style="{
            fontWeight: '600',
            cursor: 'pointer',
          }"
          @click="value = '0'"
          >{{ t("auth_wrapper.sign_in") }}</span
        >
      </p>
    </div>
  </div>
</template>

<style scoped>
.sign-in-sign-up-wrapper {
  /* Figma Authorization flow: Log in / Create account = 480 (content 400 + pad 40) */
  padding: 0 40px 40px 40px;
  overflow: auto;
  width: min(90vw, 480px);
  box-sizing: border-box;
}

.have-dont-have-account {
  display: flex;
  justify-content: center;
}

.sign-in-bottom-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 40px 0;
}

.sign-in__divider {
  width: 100%;
}

.sign-in__divider-text {
}

.sign-in_third_party {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 32px;
}

.third-party-auth_item {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  padding: 16px;
  border: 1px solid rgba(215, 217, 219, 1);
  border-radius: 12px;
  cursor: pointer;
}
</style>
